import { Link, Outlet, useRouterState } from "@tanstack/react-router";
import {
  Bell,
  Calendar,
  Contact,
  ListChecks,
  Mail,
  Settings,
  ShieldCheck,
  Smartphone,
  StickyNote,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { type NavItem, ShellFrame, SideNavLink } from "../components/shell-frame.js";
import { useShell } from "../components/shell-slots.js";

const APPS: ReadonlyArray<NavItem> = [
  { to: "/mail", key: "nav.mail", icon: Mail },
  { to: "/calendar", key: "nav.calendar", icon: Calendar },
  { to: "/contacts", key: "nav.contacts", icon: Contact },
  { to: "/tasks", key: "nav.tasks", icon: ListChecks },
  { to: "/notes", key: "nav.notes", icon: StickyNote },
];

const SETTINGS: ReadonlyArray<NavItem> = [
  { to: "/settings/mail", key: "nav.mailSettings", icon: Settings },
  { to: "/settings/devices", key: "nav.devices", icon: Smartphone },
  { to: "/settings/security", key: "nav.security", icon: ShieldCheck },
  { to: "/settings/notifications", key: "nav.notifications", icon: Bell },
];

/** The workspace: the apps on a rail (bottom bar on phones), each page fills the frame. */
export function AppShell() {
  const { t } = useTranslation();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const current = [...APPS, ...SETTINGS].find((item) => pathname.startsWith(item.to)) ?? APPS[0];
  const inSettings = pathname.startsWith("/settings");
  return (
    <ShellFrame
      title={current ? t(current.key) : t("app.name")}
      homeTo="/mail"
      rail={APPS}
      {...(inSettings ? { sideNav: <SettingsNav /> } : {})}
      drawerFooter={
        inSettings
          ? null
          : SETTINGS.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="flex h-10 items-center gap-4 rounded-full px-3 text-sm text-ink-2 hover:bg-surface-2"
              >
                <item.icon className="size-5" />
                {t(item.key)}
              </Link>
            ))
      }
    >
      <Outlet />
    </ShellFrame>
  );
}

/** The settings pages' own left column. */
function SettingsNav() {
  const { t } = useTranslation();
  const { closeDrawer } = useShell();
  return (
    <nav aria-label={t("nav.settings")} className="flex flex-col pt-1 pr-2 md:pr-3">
      <div className="pb-1 pl-6 text-xs font-medium tracking-wide text-ink-3 uppercase">
        {t("nav.settings")}
      </div>
      {SETTINGS.map((item) => (
        <SideNavLink key={item.to} item={item} onNavigate={closeDrawer} />
      ))}
    </nav>
  );
}
